/**
 * "Change question" on a note DaVinci placed under a guessed question (#668): a quiet link, then an inline chooser, then the POST (the
 * caller's). The pills are the questions the upload names, less the one the note is under, and the learner may type another label. Nothing
 * on the note changes until the server answers; the chooser stays open on a failure, so the same press is the retry.
 */

import { useState } from "react";
import { useMessages } from "../../i18n/locale.js";
import { feedbackChrome } from "./feedback-chrome.js";
import { UntrustedText } from "./untrusted-text.js";

/** The longest question label the server accepts. */
export const QUESTION_LABEL_MAX = 32;

export function ChangeQuestion(props: {
  readonly current: string | null;
  readonly offered: readonly string[];
  readonly onMove: (label: string) => Promise<boolean>;
  /** A pull of a rung is in flight on this note: a move landing while a solution is being written can cost a paid call with no delivery, so none starts. */
  readonly disabled?: boolean;
}): React.JSX.Element {
  const m = useMessages(feedbackChrome);
  const [choosing, setChoosing] = useState(false);
  const [sending, setSending] = useState(false);
  const [typed, setTyped] = useState("");
  const label = typed.trim();
  const move = (to: string): void => {
    setSending(true);
    void props.onMove(to).then(
      (moved) => {
        setSending(false);
        if (moved) {
          setChoosing(false);
          setTyped("");
        }
      },
      () => setSending(false),
    );
  };
  // Two siblings of the foot's row: the link stays among the others while the chooser is open, and the chooser takes its own row below them.
  const link = (
    <button
      type="button"
      className="fb-change-open"
      aria-expanded={choosing}
      disabled={props.disabled === true}
      onClick={() => setChoosing((open) => !open)}
    >
      {m.changeQuestion}
    </button>
  );
  if (!choosing) return link;
  return (
    <>
      {link}
      <div className="fb-change" role="group" aria-label={m.changeQuestion}>
        <p className="fb-change-head">{m.changeQuestionWhich}</p>
        <div className="fb-change-pills">
          {props.offered
            .filter((offered) => offered !== props.current)
            .map((offered) => (
              <button
                key={offered}
                type="button"
                className="button-quiet fb-change-pill"
                disabled={sending || props.disabled === true}
                onClick={() => move(offered)}
              >
                <UntrustedText text={offered} />
              </button>
            ))}
        </div>
        <form
          className="fb-change-other"
          onSubmit={(event) => {
            event.preventDefault();
            if (props.disabled !== true && label !== "" && label !== props.current) move(label);
          }}
        >
          <input
            type="text"
            className="fb-change-field"
            value={typed}
            maxLength={QUESTION_LABEL_MAX}
            placeholder={m.changeQuestionOther}
            aria-label={m.changeQuestionOther}
            onChange={(event) =>
              setTyped(event.target.value.slice(0, QUESTION_LABEL_MAX))
            }
          />
          <button
            type="submit"
            className="button-quiet fb-change-move"
            disabled={sending || props.disabled === true || label === "" || label === props.current}
          >
            {m.changeQuestionMove}
          </button>
        </form>
        <button
          type="button"
          className="fb-change-cancel"
          disabled={sending}
          onClick={() => setChoosing(false)}
        >
          {m.changeQuestionCancel}
        </button>
      </div>
    </>
  );
}
